import React, { FC, useMemo } from 'react';
import { AppManagerProps } from '../../types/appManager';
import { ExperimentsProvider } from '@wix/wix-experiments-react';
import { EditorPlatformBIProvider } from '../providers/EditorPlatformBIProvider';
import { EditorPlatformFedopsProvider } from '../providers/EditorPlatformFedopsProvider';
import { EditorPlatformI18nProvider } from '../providers/EditorPlatformI18nProvider';
import { AppManagerContainer } from './AppManagerContainer/AppManagerContainer';
import { getAppManagerPrivateAPI } from './appManagerAPI';
import { AppManagerDataProvider } from './providers/AppManagerDataProvider';
import { WixDesignSystemProvider } from '@wix/design-system';
import { AppManagerErrorBoundary } from './AppManagerErrorBoundary';
import {
  PLATFORM_BI_ENDPOINT,
  PLATFORM_HOST_INTEGRATION_BI_SRC,
} from './constants/biEvents';

export const AppManager: FC<AppManagerProps> = ({
  appsList,
  pendingAppDefIds,
  userPermissions,
  allAvailableToUpdateApps,
  apiDependencies,
  onPanelTitleReady,
  selectedAppId,
  selectedAppFromUrl,
  origin,
  locale,
  experiments,
}) => {
  const api = useMemo(
    () =>
      getAppManagerPrivateAPI(
        appsList,
        pendingAppDefIds,
        userPermissions,
        allAvailableToUpdateApps,
        apiDependencies,
      ),
    [
      appsList,
      pendingAppDefIds,
      userPermissions,
      allAvailableToUpdateApps,
      apiDependencies,
    ],
  );

  const biBrops = {
    src: PLATFORM_HOST_INTEGRATION_BI_SRC,
    endpoint: PLATFORM_BI_ENDPOINT,
  };

  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      <EditorPlatformI18nProvider locale={locale}>
        <EditorPlatformBIProvider {...biBrops}>
          <EditorPlatformFedopsProvider>
            <ExperimentsProvider experiments={experiments}>
              <AppManagerErrorBoundary>
                <AppManagerDataProvider api={api} locale={locale}>
                  <AppManagerContainer
                    data-aid="app-manager-container"
                    selectedAppId={selectedAppId}
                    selectedAppFromUrl={selectedAppFromUrl}
                    origin={origin}
                    onPanelTitleReady={onPanelTitleReady}
                  />
                </AppManagerDataProvider>
              </AppManagerErrorBoundary>
            </ExperimentsProvider>
          </EditorPlatformFedopsProvider>
        </EditorPlatformBIProvider>
      </EditorPlatformI18nProvider>
    </WixDesignSystemProvider>
  );
};
